/* Points UI. The backend adapter is deliberately supplied separately: never infer
 * monetary exchange rates or create coupons locally. See docs/points-integration.md. */
(() => {
  'use strict';
  const amountError = (raw, balance) => {
    if (!/^[1-9]\d*$/.test(raw) || !Number.isSafeInteger(Number(raw))) return 'integer';
    return Number(raw) > balance ? 'exceeds' : '';
  };
  async function loadGreetingName(node, profileAPI, authAPI) {
    const token = authAPI?.getAccessToken?.();
    if (!node || !profileAPI || !token) return;
    const showName = profile => {
      const name = typeof profile?.firstName === 'string' ? profile.firstName.trim() : '';
      if (!name) return false;
      if (node.isConnected && authAPI.getAccessToken() === token) node.textContent = name;
      return true;
    };
    try {
      if (showName(profileAPI.getCachedUserData?.())) return;
      // A cached profile without a usable name also needs a fresh request.
      const result = await profileAPI.getUserProfile(true);
      if (result?.code === 200) showName(result.data);
    } catch (_) { /* Keep the translated fallback; profile errors must not block points. */ }
  }
  function couponRedirect(redirectUrl, root, origin, destination) {
    const target = new URL(redirectUrl, origin);
    if (target.origin !== origin || !['http:', 'https:'].includes(target.protocol)) throw new Error('Invalid coupon redirect');
    // Use the same Liquid routes.root_url as the account navigation links.
    const prefix = root.replace(/\/$/, '');
    const path = target.pathname.replace(/^\/(?:en|de|fr|it|es|nl)(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
    target.pathname = prefix + (path.startsWith('/') ? path : '/' + path);
    if (destination) {
      if (!destination.code) throw new Error('Missing coupon code');
      target.pathname = prefix + '/pages/store';
      target.searchParams.delete('redirect');
      target.searchParams.set('discount', destination.code);
      target.searchParams.delete('points_cart');
      if (destination.visitedCart) target.searchParams.set('points_cart', '1');
    }
    return target.href;
  }
  function couponOrderLink(coupon, root) {
    const reference = String(coupon.orderId || '').trim();
    const id = reference.replace(/^gid:\/\/shopify\/Order\//, '');
    // The API calls orderId an order reference; accept both Shopify IDs and names.
    const name = String(coupon.orderNumber || coupon.orderName || (/^gid:\/\//.test(reference) ? '' : reference)).trim();
    const params = new URLSearchParams();
    if (/^\d+$/.test(id)) params.set('order_id', id);
    if (/^#?[\p{L}\p{N}][\p{L}\p{N} _#-]{0,127}$/u.test(name)) params.set('order_name', name);
    if (!params.size) return null;
    return root.replace(/\/$/, '') + '/pages/order-detail?' + params;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { amountError, couponRedirect, couponOrderLink, loadGreetingName };
  if (typeof document === 'undefined') return;

  function initialize(root) {
    if (root.dataset.initialized) return;
    // Section scripts can execute after DOMContentLoaded while dependencies are
    // still loading. Leave the section retryable until pagination is ready.
    if (root.dataset.mode === 'coupons' && typeof window.createPointsPagination !== 'function') return;
    root.dataset.initialized = 'true';
    const track = action => window.trackPointsEvent?.(action);
    track(root.dataset.mode === 'coupons' ? 'coupons_view' : 'redeem_view');
    const strings = JSON.parse(root.querySelector('[data-points-strings]').textContent);
    const find = (selector) => root.querySelector(selector);
    const greetingName = find('[data-points-greeting-name]');
    greetingName?.setAttribute('data-clarity-mask', 'true');
    loadGreetingName(greetingName, window.userProfileAPI, window.authAPI);
    const signOut = find('[data-points-sign-out] button');
    if (signOut) signOut.addEventListener('click', async () => {
      signOut.disabled = true;
      try {
        if (!window.authAPI?.logout) throw new Error('Logout unavailable');
        await window.authAPI.logout();
        window.userProfileAPI?.clearCachedUserData?.();
        window.location.href = root.dataset.root.replace(/\/$/, '') + '/pages/login';
      } catch (_) {
        signOut.disabled = false;
        find('[data-status]').textContent = strings.failed;
      }
    });
    const amountComponent = find('[data-points-amount-field] .component-text-input');
    if (amountComponent) {
      const field = amountComponent.querySelector('input');
      const error = amountComponent.querySelector('.component-text-input__error');
      field.dataset.amount = ''; error.dataset.inputError = '';
      error.setAttribute('aria-live', 'polite'); field.setAttribute('aria-describedby', error.id);
      amountComponent.querySelector('.component-text-input__clear-btn').setAttribute('aria-label', strings.clear_input);
    }
    // Example data is confined to Shopify's authenticated theme editor.
    const isPreview = root.dataset.uiPreview === 'true';
    const context = JSON.parse(document.querySelector('[data-points-context]').textContent);
    const api = isPreview ? {
      getBalance: async () => ({ availablePoints: 1250 }),
      quote: async ({ points, currency }) => ({ quoteId: 'preview', points, value: points, currency: currency || 'EUR', remaining: 1250 - points }),
      listCoupons: async () => (root.dataset.previewEmpty === 'true' ? [] : ['available', 'used', 'expired']).map((state) => ({
        status: state, value: 3000, currency: root.dataset.currency || 'EUR', code: 'DEMO-PRC3000',
        validFrom: '2026-08-17T00:00:00Z', validUntil: '2026-09-16T23:59:59Z',
        orderId: '1842', orderNumber: '#DEMO-1842', usedAt: '2026-08-29T14:32:00Z'
      }))
    } : new window.PointsAPI(window.PointsAPI.selectedRegion(context.region));
    const base = root.dataset.root;
    const locale = document.documentElement.lang || 'en';
    const money = (value, currency) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
    const status = (key) => { find('[data-status]').textContent = strings[key] || ''; };
    const retry = find('[data-retry]');
    const dialog = find('[data-dialog]');
    const body = find('[data-dialog-body]');
    const actions = find('[data-dialog-actions]');
    function renderBalance(value) {
      find('[data-balance]').textContent = value === null ? '—' : value.toLocaleString(locale);
      const expiry = find('[data-balance-expiry]');
      if (expiry) expiry.textContent = value === null ? '—' : context.strings.expiry_policy;
    }
    let balance = null, quote = null, busy = false, revision = 0, timer, unresolved = false;
    let activeTab = 'available', coupons = [], dialogRevision = 0, couponPage = 1, couponLoadedPage = 1, couponPages = 0, couponTotal = null;
    const couponPagination = root.dataset.mode === 'coupons' ? window.createPointsPagination(find('[data-coupon-list]'), strings, page => { if (busy) { couponPagination.update(couponPage, couponPages, couponTotal); return; } track('coupons_page_change'); couponPage = page; load(); }) : null;
    const useOperations = new Map();
    let retryAction = load;
    function element(tag, text, className) {
      const el = document.createElement(tag);
      if (text !== undefined) el.textContent = text;
      if (className) el.className = className;
      return el;
    }
    function button(key, action, primary = false) {
      const el = element('button', strings[key], 'points-hub__button' + (primary ? ' points-hub__button--primary' : ''));
      el.type = 'button'; el.addEventListener('click', action); return el;
    }
    function row(label, value, parent = body) {
      const el = element('div', undefined, 'points-hub__row');
      el.append(element('span', strings[label]), element('span', value)); parent.append(el);
    }
    let dialogTrigger = null;
    function openDialog(key, trigger = document.activeElement) {
      track('dialog_' + key);
      ++dialogRevision;
      find('[data-dialog-title]').textContent = strings[key];
      body.replaceChildren(); actions.replaceChildren();
      if (!dialog.open) { dialogTrigger = trigger; dialog.showModal(); }
    }
    function closeDialog() { if (!busy) dialog.close(); }
    find('[data-close]').addEventListener('click', closeDialog);
    dialog.addEventListener('close', () => {
      track('dialog_close');
      // Native dialog.close() can restore focus before the close event fires.
      if (document.activeElement === dialogTrigger) dialogTrigger.blur();
      dialogTrigger = null;
    });
    dialog.addEventListener('cancel', (event) => { if (busy) event.preventDefault(); });
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) closeDialog();
    });
    find('[data-points-back]')?.addEventListener('click', (event) => {
      if (document.referrer && new URL(document.referrer).origin === location.origin) {
        event.preventDefault(); history.back();
      }
    });
    retry.addEventListener('click', () => { track('retry'); retryAction(); });
    function fail(key, action = load) {
      track('load_error');
      status(key); retryAction = action; retry.hidden = false;
      if (key === 'login') {
        retry.textContent = strings.login;
        retryAction = () => {
          try { sessionStorage.setItem('points-login-return', location.pathname + location.search); } catch (_) { /* Login still works without storage. */ }
          location.href = base + 'pages/login';
        };
      } else retry.textContent = strings.retry;
    }
    function setBusy(value) {
      busy = value;
      root.setAttribute('aria-busy', String(value));
      dialog.querySelectorAll('button').forEach((el) => { el.disabled = value; });
      if (find('[data-amount]')) {
        const disabled = value || balance === null || unresolved;
        find('[data-amount]').disabled = disabled;
        amountComponent?.classList.toggle('component-text-input--disabled', disabled);
        const clear = amountComponent?.querySelector('.component-text-input__clear-btn');
        if (clear) clear.disabled = disabled;
      }
      if (find('[data-preview]')) find('[data-preview]').disabled = value || !quote || unresolved;
    }
    function resetPreview() {
      quote = null;
      find('[data-value]').textContent = '—';
      find('[data-preview]').disabled = true;
    }
    function showAmountError(error) {
      const field = find('[data-amount]');
      if (error) window.TextInputComponent.setError(field, strings[error]);
      else window.TextInputComponent.clearError(field);
      field.setAttribute('aria-invalid', String(Boolean(error)));
    }
    async function preview() {
      const requestRevision = ++revision;
      resetPreview();
      const raw = find('[data-amount]').value.trim();
      const error = amountError(raw, balance);
      showAmountError(error);
      find('[data-remaining]').textContent = error ? '—' : (balance - Number(raw)).toLocaleString(locale);
      if (error || busy || unresolved) return;
      try {
        const result = await api.quote({ points: Number(raw), currency: root.dataset.currency, country: root.dataset.country });
        if (requestRevision !== revision) return;
        if (!result || result.points !== Number(raw) || !Number.isFinite(result.value) || result.value <= 0 || !/^[A-Z]{3}$/.test(result.currency) || !Number.isSafeInteger(result.remaining) || result.remaining < 0 || !result.quoteId) throw new Error('Invalid quote');
        track('quote_success');
        quote = result;
        balance = result.remaining + result.points;
        renderBalance(balance);
        find('[data-value]').textContent = money(result.value, result.currency);
        find('[data-remaining]').textContent = result.remaining.toLocaleString(locale);
        find('[data-preview]').disabled = false;
        status(''); retry.hidden = true;
      } catch (error) {
        if (requestRevision === revision) {
          if (api.balance) {
            balance = api.balance.availablePoints;
            renderBalance(balance);
          }
          fail(error.code || 'load_error', preview);
        }
      }
    }
    function confirmPreview(event) {
      event.preventDefault();
      if (!quote || busy) return;
      openDialog('confirm_title');
      row('amount', quote.points.toLocaleString(locale));
      row('value', money(quote.value, quote.currency));
      if (api.region === 'UK') body.append(element('p', strings.exchange_notice, 'points-hub__notice'));
      row('remaining', quote.remaining.toLocaleString(locale));
      if (quote.template) {
        if (quote.template.minOrderAmount > 0) body.append(element('p', strings.minimum_spend + ' ' + money(quote.template.minOrderAmount, quote.currency)));
        if (quote.template.stackable === false) body.append(element('p', strings.not_stackable));
        if (quote.template.productScopeType && quote.template.productScopeType !== 'ALL') body.append(element('p', strings.eligible_products));
      }
      body.append(element('p', strings.redemption_validity));
      body.append(element('p', strings.redemption_balance));
      const confirm = button('confirm', () => submit(false), true);
      confirm.dataset.submitRedemption = '';
      actions.append(button('cancel', closeDialog), confirm);
    }
    async function submit(recover) {
      if (busy) return;
      if (isPreview) { closeDialog(); status('preview_notice'); return; }
      track(recover ? 'redeem_recover' : 'redeem_submit');
      setBusy(true); retry.hidden = true;
      const confirm = find('[data-submit-redemption]');
      if (confirm) { confirm.textContent = strings.loading; confirm.setAttribute('aria-busy', 'true'); }
      try {
        const result = await api.redeem(quote, recover);
        track('redeem_success');
        unresolved = false; balance = result.remainingBalance;
        renderBalance(balance);
        setBusy(false); closeDialog();
        location.href = base + 'pages/points-coupons?' + new URLSearchParams({ redeemed: '1', siteRegion: api.region });
      } catch (error) {
        track('redeem_error');
        try { unresolved = Boolean(api.pending()); } catch (_) { unresolved = true; }
        setBusy(false); closeDialog();
        fail(error.code === 'login' ? 'login' : unresolved ? 'unknown' : error.code || 'failed', unresolved ? () => submit(true) : preview);
      }
    }
    async function showDetails(coupon, trigger) {
      openDialog('details', trigger);
      const requestRevision = dialogRevision;
      if (!isPreview) {
        body.textContent = strings.loading;
        try { coupon = await api.getCouponDetails(coupon.couponId); }
        catch (error) { if (dialog.open && requestRevision === dialogRevision) { track('coupon_details_error'); body.textContent = strings[error.code] || strings.load_error; actions.append(button('retry', () => { track('coupon_details_retry'); showDetails(coupon); }), button('close', closeDialog)); } return; }
        if (!dialog.open || requestRevision !== dialogRevision) return;
        body.replaceChildren();
      }
      const list = element('ul');
      if (coupon.minOrderAmount > 0) list.append(element('li', strings.minimum_spend + ' ' + money(coupon.minOrderAmount, coupon.currency)));
      else list.append(element('li', strings.no_minimum));
      list.append(element('li', strings[coupon.stackable === false ? 'not_stackable' : 'max_coupons']));
      list.append(element('li', strings[coupon.productScopeType && coupon.productScopeType !== 'ALL' ? 'eligible_products' : 'all_products']));
      const faq = element('a', strings.faq_more, 'points-hub__link');
      faq.href = base + 'pages/points-explanation';
      const help = element('p');
      help.append(faq);
      body.append(list, help); actions.append(button('close', closeDialog));
    }
    function date(value) {
      if (!value) return '—';
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) + ' (' + strings.local_time + ')';
    }
    function renderCoupons() {
      const list = find('[data-coupon-list]'); list.replaceChildren();
      root.querySelectorAll('[data-tab]').forEach((el) => {
        const selected = el.dataset.tab === activeTab;
        el.setAttribute('aria-pressed', String(selected));
        if (selected) list.setAttribute('aria-labelledby', el.id);
      });
      const history = find('[data-coupon-history]');
      history.classList.toggle('is-selected', activeTab !== 'available');
      find('[data-coupon-history-label]').textContent = strings[activeTab];
      // The API status query owns tab membership. A stale AVAILABLE coupon may
      // already be expired locally; keep it visible with its disabled expiry state.
      const items = coupons.filter((coupon) => coupon.status !== 'recalled').slice(0, 5);
      couponPagination.update(couponPage, couponPages, couponTotal);
      if (!items.length) {
        const empty = element('div', undefined, 'points-hub__empty');
        const icon = element('img', undefined, 'points-hub__empty-icon');
        icon.src = root.dataset.emptyIcon; icon.alt = ''; icon.width = 72; icon.height = 64;
        empty.append(icon);
        empty.append(element('h3', strings['empty_' + activeTab]));
        empty.append(element('p', strings.empty_description));
        list.append(empty);
      }
      items.forEach((coupon) => {
        const card = element('article', undefined, 'points-hub__card');
        if (coupon.status !== 'available') {
          card.classList.add('points-hub__coupon--inactive');
          card.append(element('span', strings[coupon.status], 'points-hub__coupon-stamp'));
        }
        const heading = element('h3', undefined, 'points-hub__coupon-heading');
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 24 24'); icon.setAttribute('aria-hidden', 'true'); icon.setAttribute('focusable', 'false');
        const outline = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        outline.setAttribute('d', 'M4 4.5h16a1 1 0 0 1 1 1V9a3 3 0 0 0 0 6v3.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V15a3 3 0 0 0 0-6V5.5a1 1 0 0 1 1-1Z M6.5 7v1m0 3.5v1m0 3.5v1 M17.5 7v1m0 3.5v1m0 3.5v1 M10 16V8h3a2.5 2.5 0 0 1 0 5h-3 M9 16h2');
        icon.append(outline);
        heading.append(icon, element('span', (coupon.discountType === 'PERCENT' ? coupon.value + '%' : money(coupon.value, coupon.currency)) + ' ' + strings.store_coupon));
        card.append(heading);
        const details = button('details', () => showDetails(coupon, details)); details.className = 'points-hub__link';
        card.append(details);
        if (coupon.status === 'used') {
          row('used_at', coupon.usedAt ? new Date(coupon.usedAt).toLocaleString(locale) : '—', card);
        } else {
          if (coupon.validFrom) row('valid', date(coupon.validFrom) + ' – ' + date(coupon.validUntil), card);
          else row('expires_on', date(coupon.validUntil), card);
        }
        const footer = element('div', undefined, 'points-hub__coupon-footer');
        const code = element('span', coupon.code || '—', 'points-hub__code');
        code.setAttribute('data-clarity-mask', 'true');
        code.setAttribute('aria-label', strings.code);
        if (coupon.status === 'available') card.append(code);
        const controls = element('div', undefined, 'points-hub__actions');
        if (coupon.status === 'available') {
          const copy = button('copy', async () => {
            try {
              await navigator.clipboard.writeText(coupon.code);
              track('coupon_copy_success');
              if (window.showToast) window.showToast(strings.copied, 'normal', 3000);
              else status('copied');
            } catch (_) {
              track('coupon_copy_error');
              if (window.showToast) window.showToast(strings.copy_error, 'error', 5000);
              else status('copy_error');
            }
          });
          copy.disabled = !coupon.code; controls.append(copy);
          const operationKey = String(coupon.couponId);
          const operation = useOperations.get(operationKey) || { requestId: null, busy: false };
          useOperations.set(operationKey, operation);
          const use = button('use', async () => {
            if (isPreview) { status('preview_notice'); return; }
            if (operation.busy) return;
            track('coupon_use_submit');
            operation.busy = true;
            use.disabled = true;
            try {
              operation.requestId ||= crypto.randomUUID();
              const result = await api.useCoupon(coupon.couponId, operation.requestId);
              let visitedCart = false;
              try { visitedCart = sessionStorage.getItem('points-return-to-cart') === '1'; } catch (_) { /* Storage unavailable: use Store. */ }
              const redirect = couponRedirect(result.redirectUrl, base, location.origin, { code: result.discountCode, visitedCart });
              window.commerceAttribution?.markPoints(result.discountCode);
              track('coupon_use_success');
              location.href = redirect;
            } catch (error) { track('coupon_use_error'); status(error.code || 'load_error'); }
            finally { operation.busy = false; renderCoupons(); }
          }, true);
          use.disabled = operation.busy;
          controls.append(use);
        }
        if (coupon.status === 'available') { footer.append(controls); card.append(footer); }
        list.append(card);
      });
    }
    async function load() {
      if (busy) return;
      const currentUrl = new URL(location.href);
      if (root.dataset.mode === 'coupons' && currentUrl.searchParams.get('redeemed') === '1') {
        currentUrl.searchParams.delete('redeemed');
        history.replaceState(history.state, '', currentUrl.pathname + currentUrl.search + currentUrl.hash);
        window.showToast(strings.success, 'normal', 3000);
        const toast = document.querySelector('.global-toast');
        if (toast) { toast.setAttribute('role', 'status'); toast.setAttribute('aria-live', 'polite'); }
      }
      const loadingRegion = find('region-loading');
      loadingRegion?.setAttribute('loading', '');
      status(''); retry.hidden = true;
      if (context.region === 'Global' || context.region === 'CA') {
        if (root.dataset.mode === 'redeem') {
          balance = 0;
          renderBalance(0);
          find('[data-remaining]').textContent = '0';
        } else {
          coupons = []; couponPages = 0; couponTotal = 0;
          renderCoupons();
        }
        setBusy(false);
        loadingRegion?.removeAttribute('loading');
        return;
      }
      if (!api) {
        status('unavailable');
        root.querySelectorAll('[data-tab]').forEach((el) => { el.disabled = true; });
        loadingRegion?.removeAttribute('loading');
        return;
      }
      try {
        if (root.dataset.mode === 'redeem') {
          balance = null; setBusy(true);
          const cachedBalance = !isPreview ? await api.getCachedBalance() : null;
          renderBalance(cachedBalance?.availablePoints ?? null);
          if (cachedBalance) loadingRegion?.removeAttribute('loading');
          const result = await api.getBalance();
          if (!isPreview && !api.pending()) await api.loadCouponTemplates();
          if (!Number.isSafeInteger(result.availablePoints) || result.availablePoints < 0) throw new Error('Invalid balance');
          balance = result.availablePoints;
          renderBalance(balance);
          unresolved = !isPreview && Boolean(api.pending());
          setBusy(false); status('');
          if (unresolved) { fail('unknown', () => submit(true)); return; }
          if (find('[data-amount]').value.trim()) await preview();
        } else {
          setBusy(true);
          couponPagination.setBusy(true);
          const cached = !isPreview ? await api.getCachedCoupons(couponPage, activeTab.toUpperCase()) : null;
          let displayed = null;
          if (cached) {
            coupons = cached.records; couponPages = cached.pages; couponTotal = cached.total;
            couponLoadedPage = couponPage;
            displayed = JSON.stringify(cached);
            renderCoupons();
            loadingRegion?.removeAttribute('loading');
          }
          const response = await api.listCoupons(couponPage, activeTab.toUpperCase());
          const result = Array.isArray(response) ? response.filter(coupon => coupon.status === activeTab) : response.records;
          couponPages = Array.isArray(response) ? 1 : response.pages;
          couponTotal = Array.isArray(response) ? result.length : response.total;
          if (!Array.isArray(result) || result.some((coupon) => !['available', 'used', 'expired'].includes(coupon.status) || !Number.isFinite(coupon.value) || !/^[A-Z]{3}$/.test(coupon.currency))) throw new Error('Invalid coupons');
          track('coupons_load_success');
          couponLoadedPage = couponPage;
          coupons = result;
          if (displayed !== JSON.stringify(response)) renderCoupons();
          setBusy(false);
          status('');
        }
      } catch (error) { setBusy(false); couponPagination?.update(couponLoadedPage, couponPages, couponTotal); fail(error.code || 'load_error'); }
      finally { couponPagination?.setBusy(false); loadingRegion?.removeAttribute('loading'); }
    }
    if (find('[data-amount]')) {
      find('[data-amount]').addEventListener('input', () => { ++revision; resetPreview(); clearTimeout(timer); timer = setTimeout(preview, 250); });
      find('[data-amount]').addEventListener('input:clear', () => {
        track('amount_clear');
        ++revision; clearTimeout(timer); resetPreview(); showAmountError('');
        find('[data-remaining]').textContent = '—';
      });
      find('[data-amount]').addEventListener('blur', (event) => {
        if (event.relatedTarget?.closest('.component-text-input__clear-btn')) return;
        const raw = find('[data-amount]').value.trim();
        showAmountError(raw ? amountError(raw, balance) : '');
      });
      find('[data-redeem-form]').addEventListener('submit', confirmPreview);
    } else {
      root.querySelectorAll('[data-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          const history = find('[data-coupon-history]');
          history.open = false;
          if (tab.dataset.tab !== 'available') history.querySelector('summary').focus();
          if (busy || activeTab === tab.dataset.tab) return;
          track('coupons_tab_' + tab.dataset.tab);
          activeTab = tab.dataset.tab; couponPage = 1; couponLoadedPage = 1; couponPages = 0; couponTotal = null; coupons = []; renderCoupons(); load();
        });
      });
      const history = find('[data-coupon-history]');
      history.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); history.open = false; history.querySelector('summary').focus(); }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault(); history.open = true;
        const items = [...history.querySelectorAll('[data-tab]')], index = items.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : index < 0 ? (event.key === 'ArrowUp' ? items.length - 1 : 0) : (index + (event.key === 'ArrowUp' ? items.length - 1 : 1)) % items.length;
        items[next].focus();
      });
      document.addEventListener('click', event => { if (!history.contains(event.target)) history.open = false; });
      find('[data-how]').addEventListener('click', () => {
        openDialog('how'); const list = element('ol');
        strings.how_steps.split('|').forEach((step) => list.append(element('li', step)));
        body.append(list); actions.append(button('close', closeDialog, true));
      });
    }
    load();
  }
  const start = () => document.querySelectorAll('[data-points-hub]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('points:pagination-ready', start);
})();
